import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { signOut } from '../store/slices/authSlice'
import Sidebar from '../components/(admin)/Sidebar'
import PageTopBar from '../components/(admin)/PageTopBar'
import editimg from '../assets/images/edit.png'
import deleteimg from '../assets/images/delete.png'
import { colors } from '../constants/colors'
import { 
  fetchTeamMembers, 
  fetchTeamInvitations,
  inviteMember,
  deleteTeamMember,
  resendInvitation,
  updateTeamMember,
  clearError,
  clearSuccess 
} from '../store/slices/teamSlice'

const TeamManagement = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { teamMembers, invitations, loading, error, success } = useSelector(state => state.team)
  const acceptedMembers = teamMembers.filter(m => m.status === 'accepted')
  const pendingInvitations = invitations.filter(inv => inv.status !== 'accepted')
  const [activeTab, setActiveTab] = useState('team')
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingMember, setEditingMember] = useState(null)
  const [inviteForm, setInviteForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'Admin',
    sendEmail: true
  })
  const [editForm, setEditForm] = useState({
    role: ''
  })

  useEffect(() => {
    dispatch(fetchTeamMembers())
    dispatch(fetchTeamInvitations())
  }, [dispatch])

  // Debug: Log the invitations data
  useEffect(() => {
    if (invitations.length > 0) {
      console.log('Invitations data:', invitations)
    }
  }, [invitations])

  useEffect(() => {
    if (error) {
      console.error('Team error:', error)
    }
    if (success) {
      console.log('Team success:', success)
      setTimeout(() => dispatch(clearSuccess()), 3000)
    }
  }, [error, success, dispatch])

  const handleDeleteMember = (memberId) => {
    if (window.confirm('Are you sure you want to remove this team member?')) {
      dispatch(deleteTeamMember(memberId))
    }
  }

  const handleResendInvite = (invitationId) => {
    dispatch(resendInvitation(invitationId))
  }

  const handleSignOut = async () => {
    try {
      await dispatch(signOut()).unwrap()
      navigate('/signin')
    } catch (err) {
      console.error('Logout failed:', err)
    }
  }

  const handleInviteMembers = () => {
    dispatch(clearError())
    setShowInviteModal(true)
  }

  const handleCloseModal = () => {
    setShowInviteModal(false)
    setInviteForm({
      firstName: '',
      lastName: '',
      email: '',
      role: 'Admin',
      sendEmail: true
    })
  }

  const handleFormChange = (field, value) => {
    setInviteForm(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSendInvite = async () => {
    const { email, firstName, lastName, role } = inviteForm
    if (!email?.trim()) {
      dispatch(clearError())
      return
    }
    const name = [firstName, lastName].filter(Boolean).join(' ').trim()
    try {
      await dispatch(inviteMember({ email: email.trim(), name, role })).unwrap()
      dispatch(fetchTeamMembers())
      dispatch(fetchTeamInvitations())
      handleCloseModal()
    } catch (_) {
      // error stored in state.team.error
    }
  }

  const handleEditMember = (member) => {
    console.log('Editing member:', member._id)
    console.log('Business ID:', member.business_id)
    setEditingMember(member)
    setEditForm({
      role: member.teamRole || member.role
    })
    setShowEditModal(true)
  }

  const handleCloseEditModal = () => {
    setShowEditModal(false)
    setEditingMember(null)
    setEditForm({ role: '' })
  }

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleUpdateMember = async () => {
    if (!editingMember) return
    
    try {
      console.log('Updating member:', editingMember._id, 'with role:', editForm.role)
      
      // Use the simpler team route that doesn't require business ID
      await dispatch(updateTeamMember({ 
        memberId: editingMember._id, 
        updateData: { role: editForm.role }
      }))
      
      // Refetch team members to get updated data
      dispatch(fetchTeamMembers())
      
      handleCloseEditModal()
    } catch (error) {
      console.error('Error updating member:', error)
    }
  }


  const formatDate = (dateString) => {
    if (!dateString) return 'Never'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { 
      month: 'numeric', 
      day: 'numeric', 
      year: '2-digit' 
    })
  }

  const getRoleColor = (role) => {
    switch (role) {
      case 'Owner':
        return { backgroundColor: '#DAECFF', color: colors.text.primary }
      case 'Agent':
        return { backgroundColor: '#9EFFDB', color: colors.text.primary }
      case 'Staff':
        return { backgroundColor: '#ffe599', color: colors.text.primary }
      default:
        return { backgroundColor: colors.background.secondary, color: colors.text.primary }
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'joined':
        return { backgroundColor: '#D1FAE5', color: '#065F46' }
      case 'pending':
        return { backgroundColor: '#FEF3C7', color: '#92400E' }
      case 'failed':
        return { backgroundColor: '#FEE2E2', color: '#991B1B' }
      case 'accepted':
        return { backgroundColor: '#D1FAE5', color: '#065F46' }
      case 'rejected':
        return { backgroundColor: '#FEE2E2', color: '#991B1B' }
      default:
        return { backgroundColor: colors.background.secondary, color: colors.text.primary }
    }
  }

  const containerStyle = {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: colors.background.light
  }

  const mainContentStyle = {
    flexGrow: 1,
    marginLeft: '280px',
    padding: '20px',
    backgroundColor: colors.background.light
  }

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px'
  }

  const titleStyle = {
    fontSize: '32px',
    fontWeight: '700',
    color: colors.text.primary,
    margin: 0
  }

  const inviteButtonStyle = {
    backgroundColor: colors.primary,
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'background-color 0.2s ease'
  }

  const tabsStyle = {
    display: 'flex',
    marginBottom: '20px',
    borderBottom: `1px solid ${colors.border.light}`
  }

  const tabStyle = {
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    border: 'none',
    color: colors.text.secondary,
    borderBottom: '3px solid transparent',
    transition: 'all 0.2s ease'
  }

  const activeTabStyle = {
    ...tabStyle,
    color: colors.primary,
    borderBottom: `3px solid ${colors.primary}`
  }

  const tableContainerStyle = {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
    marginBottom: '20px'
  }

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse'
  }

  const thStyle = {
    padding: '16px 24px',
    backgroundColor: colors.background.secondary,
    color: colors.text.secondary,
    fontSize: '14px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    textAlign: 'left',
    borderBottom: `1px solid ${colors.border.light}`
  }

  const tdStyle = {
    padding: '16px 24px',
    fontSize: '15px',
    color: colors.text.primary,
    borderBottom: `1px solid ${colors.border.light}`
  }

  const roleTagStyle = (role) => {
    const baseStyle = {
      padding: '6px 12px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: '500',
      textTransform: 'uppercase',
      display: 'inline-block',
      letterSpacing: '0.5px'
    }
    return { ...baseStyle, ...getRoleColor(role) }
  }

  const actionButtonStyle = {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '4px',
    marginRight: '8px',
    transition: 'background-color 0.2s ease'
  }

  const rolePermissionStyle = {
    backgroundColor: 'white',
    padding: '24px',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
  }

  const rolePermissionTitleStyle = {
    fontSize: '20px',
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: '16px'
  }

  const roleTagsContainerStyle = {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px'
  }

  const roleDescriptionStyle = {
    fontSize: '14px',
    color: colors.text.secondary,
    lineHeight: '1.5'
  }

  const noMembersStyle = {
    textAlign: 'center',
    padding: '40px',
    color: colors.text.secondary,
    fontSize: '16px'
  }

  const loadingStyle = {
    textAlign: 'center',
    padding: '40px',
    color: colors.text.secondary,
    fontSize: '16px'
  }

  // Modal styles
  const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  }

  const modalStyle = {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '30px',
    width: '500px',
    maxWidth: '100vw',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
  }

  const modalTitleStyle = {
    fontSize: '30px',
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: '30px'
  }

  const formGroupStyle = {
    marginBottom: '18px'
  }

  const labelStyle = {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: '8px'
  }

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    border: `1px solid`,
    borderColor: "#BFBFBF",
    borderRadius: '6px',
    fontSize: '14px',
    color: colors.text.primary,
    backgroundColor: 'white',
    boxSizing: 'border-box'
  }

  const disabledInputStyle = {
    ...inputStyle,
    backgroundColor: '#F5F5F5',
    color: '#999999',
    cursor: 'not-allowed'
  }

  const selectStyle = {
    ...inputStyle,
    cursor: 'pointer'
  }

  const checkboxContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '16px'
  }

  const checkboxStyle = {
    width: '16px',
    height: '16px',
    cursor: 'pointer'
  }

  const checkboxLabelStyle = {
    fontSize: '14px',
    color: colors.text.primary,
    cursor: 'pointer'
  }

  const modalButtonsStyle = {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '24px'
  }

  const cancelButtonStyle = {
    padding: '10px 20px',
    border: `1px solid `,
    borderColor: "#BFBFBF",
    borderRadius: '6px',
    backgroundColor: 'white',
    color: colors.text.primary,
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  }

  const sendButtonStyle = {
    padding: '10px 20px',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: colors.primary,
    color: 'white',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  }

  return (
    <div style={containerStyle}>
      <Sidebar />
      <div style={mainContentStyle}>
        {/* Header */}
        <PageTopBar
          title="Team Management"
          onAiConfig={() => navigate('/ai-configuration')}
          onEmbeddingGuide={() => navigate('/embedding-guide')}
          onSignOut={handleSignOut}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '24px' }}>
          <button style={inviteButtonStyle} onClick={handleInviteMembers}>
            <span>+</span>
            Invite Members
          </button>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div style={{
            backgroundColor: '#D1FAE5',
            color: '#065F46',
            padding: '12px 16px',
            borderRadius: '8px',
            margin: '16px 0',
            border: '1px solid #A7F3D0',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            ✅ {success}
          </div>
        )}
        {error && (
          <div style={{
            backgroundColor: '#FEE2E2',
            color: '#991B1B',
            padding: '12px 16px',
            borderRadius: '8px',
            margin: '16px 0',
            border: '1px solid #FCA5A5',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            ❌ {error}
          </div>
        )}

        {/* Tabs */}
        <div style={tabsStyle}>
          <button 
            style={activeTab === 'team' ? activeTabStyle : tabStyle}
            onClick={() => setActiveTab('team')}
          >
            Team Management
          </button>
          <button 
            style={activeTab === 'invitations' ? activeTabStyle : tabStyle}
            onClick={() => setActiveTab('invitations')}
          >
            Invitation Management
          </button>
        </div>

        {/* Data Table */}
        <div style={tableContainerStyle}>
          {loading ? (
            <div style={loadingStyle}>Loading...</div>
          ) : activeTab === 'team' ? (
            acceptedMembers.length === 0 ? (
              <div style={noMembersStyle}>No members in team</div>
            ) : (
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>File Name</th>
                    <th style={thStyle}>Role</th>
                    <th style={thStyle}>Last Active</th>
                    <th style={thStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {acceptedMembers.map((member) => (
                    <tr key={member._id}>
                      <td style={tdStyle}>
                        {member.user_id?.email || 'Unknown User'}
                      </td>
                      <td style={tdStyle}>
                        <span style={roleTagStyle(member.teamRole)}>
                          {member.teamRole}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        {formatDate(member.updated_at)}
                      </td>
                      <td style={tdStyle}>
                        <button 
                          style={actionButtonStyle}
                          onClick={() => handleDeleteMember(member._id)}
                          onMouseEnter={(e) => e.target.style.backgroundColor = colors.background.secondary}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                        >
                          <img 
                            src={deleteimg} 
                            alt="Delete" 
                            style={{ width: '16px', height: '16px' }}
                          />
                        </button>
                        <button 
                          style={actionButtonStyle}
                          onClick={() => handleEditMember(member)}
                          onMouseEnter={(e) => e.target.style.backgroundColor = colors.background.secondary}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                        >
                          <img 
                            src={editimg} 
                            alt="Edit" 
                            style={{ width: '16px', height: '16px' }}
                          />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : (
            pendingInvitations.length === 0 ? (
              <div style={noMembersStyle}>No pending invitations</div>
            ) : (
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>File Name</th>
                    <th style={thStyle}>Role</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingInvitations.map((invitation) => (
                    <tr key={invitation._id}>
                      <td style={tdStyle}>
                        {invitation.user?.email || invitation.user?.name || invitation.email || 'Unknown User'}
                      </td>
                      <td style={tdStyle}>
                        <span style={roleTagStyle(invitation.teamRole || invitation.role)}>
                          {invitation.teamRole || invitation.role}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <span style={{
                          ...roleTagStyle(invitation.status),
                          ...getStatusColor(invitation.status)
                        }}>
                          {invitation.status}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <button 
                          style={{
                            color: colors.primary,
                            textDecoration: 'underline',
                            fontSize: '14px',
                            fontWeight: '500',
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px 8px'
                          }}
                          onClick={() => handleResendInvite(invitation._id)}
                        >
                          Resend Invite
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
        </div>

        {/* Role & Permission Section */}
        <div style={rolePermissionStyle}>
          <h3 style={rolePermissionTitleStyle}>Role & Permission</h3>
          <div style={roleTagsContainerStyle}>
            <span style={roleTagStyle('Owner')}>Owner</span>
            <span style={roleTagStyle('Agent')}>Agent</span>
            <span style={roleTagStyle('Staff')}>Staff</span>
          </div>
          <div style={roleDescriptionStyle}>
            <div><strong>Owner:</strong> Full permissions, non-admin request permissions.</div>
            <div><strong>Agent:</strong> Can manage specific projects and tasks.</div>
            <div><strong>Staff:</strong> Limited access, primarily for viewing and basic tasks.</div>
          </div>
        </div>
      </div>

      {/* Invite Members Modal */}
      {showInviteModal && (
        <div style={modalOverlayStyle} onClick={handleCloseModal}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <h2 style={modalTitleStyle}>Invite Members</h2>
            
            <div style={formGroupStyle}>
              <label style={labelStyle}>First Name</label>
              <input
                type="text"
                style={inputStyle}
                placeholder="First Name"
                value={inviteForm.firstName}
                onChange={(e) => handleFormChange('firstName', e.target.value)}
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Last Name</label>
              <input
                type="text"
                style={inputStyle}
                placeholder="Last Name"
                value={inviteForm.lastName}
                onChange={(e) => handleFormChange('lastName', e.target.value)}
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Email</label>
              <input
                type="email"
                style={inputStyle}
                placeholder="Email"
                value={inviteForm.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Role</label>
              <select
                style={selectStyle}
                value={inviteForm.role}
                onChange={(e) => handleFormChange('role', e.target.value)}
              >
                <option value="Admin">Admin</option>
                <option value="Owner">Owner</option>
                <option value="Agent">Agent</option>
                <option value="Staff">Staff</option>
              </select>
            </div>

            <div style={checkboxContainerStyle}>
              <input
                type="checkbox"
                style={checkboxStyle}
                checked={inviteForm.sendEmail}
                onChange={(e) => handleFormChange('sendEmail', e.target.checked)}
              />
              <label style={checkboxLabelStyle}>
                Send Invite email to this member
              </label>
            </div>

            {error && (
              <div style={{ color: '#dc2626', fontSize: 14, marginBottom: 12 }}>
                {error}
              </div>
            )}

            <div style={modalButtonsStyle}>
              <button style={cancelButtonStyle} onClick={handleCloseModal} disabled={loading}>
                Cancel
              </button>
              <button style={sendButtonStyle} onClick={handleSendInvite} disabled={loading}>
                {loading ? 'Sending...' : 'Send Invite'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Member Modal */}
      {showEditModal && editingMember && (
        <div style={modalOverlayStyle} onClick={handleCloseEditModal}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <h2 style={modalTitleStyle}>Edit Member</h2>
            
            <div style={formGroupStyle}>
              <label style={labelStyle}>Name</label>
              <input
                type="text"
                style={disabledInputStyle}
                value={editingMember.user_id?.email || 'Unknown User'}
                disabled
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Current Role</label>
              <input
                type="text"
                style={disabledInputStyle}
                value={editingMember.teamRole || editingMember.role}
                disabled
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Change Role</label>
              <select
                style={selectStyle}
                value={editForm.role}
                onChange={(e) => handleEditFormChange('role', e.target.value)}
              >
                <option value="Owner">Owner</option>
                <option value="Agent">Agent</option>
                <option value="Staff">Staff</option>
              </select>
            </div>

            <div style={modalButtonsStyle}>
              <button style={cancelButtonStyle} onClick={handleCloseEditModal}>
                Cancel
              </button>
              <button style={sendButtonStyle} onClick={handleUpdateMember}>
                Edit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TeamManagement